import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { api, BACKEND_URL } from "../lib/api";
import { useAuth } from "./AuthContext";
import { toast } from "sonner";

const CallCtx = createContext(null);
export const useCall = () => useContext(CallCtx);

const WS_URL = BACKEND_URL.replace(/^http/, "ws") + "/api/ws/calls";

function ringtone() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const beep = (t) => { const o = ctx.createOscillator(); const g = ctx.createGain(); o.connect(g); g.connect(ctx.destination); o.frequency.value = 660; g.gain.setValueAtTime(0.0001, t); g.gain.exponentialRampToValueAtTime(0.2, t + 0.02); g.gain.exponentialRampToValueAtTime(0.0001, t + 0.5); o.start(t); o.stop(t + 0.55); };
    const id = setInterval(() => { const t = ctx.currentTime; beep(t); beep(t + 0.7); }, 2200);
    const t = ctx.currentTime; beep(t); beep(t + 0.7);
    return () => { clearInterval(id); ctx.close(); };
  } catch { return () => {}; }
}

export function CallProvider({ children }) {
  const { user } = useAuth();
  const ws = useRef(null);
  const pcs = useRef({});
  const local = useRef(null);
  const screen = useRef(null);
  const ice = useRef([{ urls: ["stun:stun.l.google.com:19302"] }]);
  const [incoming, setIncoming] = useState(null);
  const [call, setCall] = useState(null);
  const [streams, setStreams] = useState({});
  const [localStream, setLocalStream] = useState(null);
  const [muted, setMuted] = useState(false);
  const [camOff, setCamOff] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [chat, setChat] = useState([]);
  const [peerState, setPeerState] = useState({});
  const callRef = useRef(null);
  callRef.current = call;

  const send = useCallback((m) => { if (ws.current?.readyState === 1) ws.current.send(JSON.stringify(m)); }, []);

  const getMedia = useCallback(async (kind) => {
    if (local.current) return local.current;
    let prefs = {}; try { prefs = JSON.parse(localStorage.getItem("velocity_devices") || "{}"); } catch {}
    const audio = prefs.mic ? { deviceId: { ideal: prefs.mic } } : true;
    const video = kind === "video" ? { width: 1280, height: 720, ...(prefs.cam ? { deviceId: { ideal: prefs.cam } } : {}) } : false;
    const s = await navigator.mediaDevices.getUserMedia({ audio, video });
    local.current = s; setLocalStream(s); return s;
  }, []);

  const addStream = (uid, s) => setStreams((p) => ({ ...p, [uid]: s }));
  const dropStream = (uid) => setStreams((p) => { const n = { ...p }; delete n[uid]; return n; });

  const pcFor = useCallback((uid, target) => {
    if (pcs.current[uid]) return pcs.current[uid];
    const pc = new RTCPeerConnection({ iceServers: ice.current });
    pcs.current[uid] = pc;
    local.current?.getTracks().forEach((t) => pc.addTrack(t, local.current));
    pc.ontrack = (e) => addStream(uid, e.streams[0]);
    pc.onicecandidate = (e) => { if (e.candidate) send({ type: "signal", to: uid, ...target, data: { candidate: e.candidate } }); };
    pc.onconnectionstatechange = () => { if (["failed", "closed"].includes(pc.connectionState)) dropStream(uid); };
    return pc;
  }, [send]);

  const offerTo = useCallback(async (uid, target) => {
    const pc = pcFor(uid, target);
    const offer = await pc.createOffer(); await pc.setLocalDescription(offer);
    send({ type: "signal", to: uid, ...target, data: { sdp: pc.localDescription } });
  }, [pcFor, send]);

  const teardown = useCallback(() => {
    Object.values(pcs.current).forEach((pc) => pc.close()); pcs.current = {};
    local.current?.getTracks().forEach((t) => t.stop()); local.current = null;
    screen.current?.getTracks().forEach((t) => t.stop()); screen.current = null;
    setLocalStream(null); setStreams({}); setMuted(false); setCamOff(false); setSharing(false); setChat([]); setPeerState({});
  }, []);

  const hangup = useCallback(async () => {
    const c = callRef.current; if (!c) return;
    if (c.room) send({ type: "room.leave", room: c.room });
    else { try { await api.post(`/calls/${c.call_id}/${c.status === "ringing" ? "cancel" : "end"}`); } catch {} }
    teardown(); setCall(null);
  }, [send, teardown]);

  const onSignal = useCallback(async (m) => {
    const c = callRef.current; if (!c) return;
    if (m.room ? m.room !== c.room : m.call_id !== c.call_id) return;
    const target = m.room ? { room: m.room } : { call_id: m.call_id };
    const pc = pcFor(m.from, target);
    if (m.data.sdp) {
      await pc.setRemoteDescription(new RTCSessionDescription(m.data.sdp));
      if (m.data.sdp.type === "offer") { const ans = await pc.createAnswer(); await pc.setLocalDescription(ans); send({ type: "signal", to: m.from, ...target, data: { sdp: pc.localDescription } }); }
    } else if (m.data.candidate) { try { await pc.addIceCandidate(new RTCIceCandidate(m.data.candidate)); } catch {} }
  }, [pcFor, send]);

  useEffect(() => {
    if (!user) return;
    let stop = false; let retry;
    const connect = () => {
      const s = new WebSocket(WS_URL); ws.current = s;
      s.onmessage = async (e) => {
        const m = JSON.parse(e.data);
        if (m.type === "hello") ice.current = m.ice_servers;
        else if (m.type === "call.incoming") { if (callRef.current) return; setIncoming(m.call); }
        else if (m.type === "call.accepted") { const c = callRef.current; if (c && c.call_id === m.call_id) { setCall({ ...c, status: "active", since: Date.now() }); await offerTo(c.other_id, { call_id: c.call_id }); } }
        else if (m.type === "call.ended") { setIncoming((i) => (i && i.call_id === m.call_id ? null : i)); const c = callRef.current; if (c && c.call_id === m.call_id) { teardown(); setCall(null); toast.message(m.status === "declined" ? "Call declined" : m.status === "missed" ? "No answer" : "Call ended"); } }
        else if (m.type === "signal") onSignal(m);
        else if (m.type === "room.joined") { setCall((c) => (c ? { ...c, status: "active", since: Date.now(), peers: m.peers } : c)); for (const p of m.peers) await offerTo(p.user_id, { room: m.room }); }
        else if (m.type === "room.peer_joined") setCall((c) => (c && c.room === m.room ? { ...c, peers: [...(c.peers || []).filter((p) => p.user_id !== m.peer.user_id), m.peer] } : c));
        else if (m.type === "room.peer_left") { pcs.current[m.user_id]?.close(); delete pcs.current[m.user_id]; dropStream(m.user_id); setCall((c) => (c && c.room === m.room ? { ...c, peers: (c.peers || []).filter((p) => p.user_id !== m.user_id) } : c)); }
        else if (m.type === "room.error") { toast.error(m.error); teardown(); setCall(null); }
        else if (m.type === "room.chat") setChat((c) => [...c.slice(-199), m]);
        else if (m.type === "room.state") setPeerState((p) => ({ ...p, [m.user_id]: { muted: m.muted, cam_off: m.cam_off, sharing: m.sharing } }));
      };
      s.onclose = () => { if (!stop) retry = setTimeout(connect, 3000); };
    };
    connect();
    const ping = setInterval(() => send({ type: "ping" }), 25000);
    return () => { stop = true; clearTimeout(retry); clearInterval(ping); ws.current?.close(); };
  }, [user, offerTo, onSignal, teardown, send]);

  useEffect(() => { if (!incoming) return; const stopRing = ringtone(); return stopRing; }, [incoming]);

  const startCall = useCallback(async (to, kind = "video") => {
    if (callRef.current) return toast.error("You're already on a call");
    try {
      await getMedia(kind);
      const { data } = await api.post("/calls/start", { to_user_id: to.user_id, kind });
      setCall({ call_id: data.call_id, kind, status: "ringing", other_id: to.user_id, other_name: to.name || data.to_name, outgoing: true, callee_online: data.callee_online });
      if (!data.callee_online) toast.message(`${to.name || "Member"} isn't online — we'll leave them a missed call`);
    } catch (e) { teardown(); toast.error(e?.response?.data?.detail || (e.name === "NotAllowedError" ? "Allow camera/microphone access to call" : "Could not start call")); }
  }, [getMedia, teardown]);

  const accept = useCallback(async () => {
    const c = incoming; if (!c) return; setIncoming(null);
    try { await getMedia(c.kind); await api.post(`/calls/${c.call_id}/accept`); setCall({ call_id: c.call_id, kind: c.kind, status: "active", other_id: c.from_user_id, other_name: c.from_name, since: Date.now() }); }
    catch (e) { teardown(); toast.error(e?.response?.data?.detail || "Could not answer"); }
  }, [incoming, getMedia, teardown]);

  const decline = useCallback(async () => { const c = incoming; setIncoming(null); if (c) api.post(`/calls/${c.call_id}/decline`).catch(() => {}); }, [incoming]);

  const joinRoom = useCallback(async (slug, name, kind = "video") => {
    if (callRef.current) return toast.error("Leave your current call first");
    try { await getMedia(kind); setCall({ room: slug, room_name: name, kind, status: "joining", peers: [] }); send({ type: "room.join", room: slug }); }
    catch (e) { teardown(); toast.error(e.name === "NotAllowedError" ? "Allow camera/microphone access to join" : "Could not join room"); }
  }, [getMedia, send, teardown]);

  const sendChat = useCallback((text) => { const c = callRef.current; if (c?.room && text.trim()) send({ type: "room.chat", room: c.room, text: text.trim() }); }, [send]);
  const broadcastState = (st) => { const c = callRef.current; if (c?.room) send({ type: "room.state", room: c.room, ...st }); };

  const toggleMute = () => { local.current?.getAudioTracks().forEach((t) => (t.enabled = muted)); setMuted(!muted); broadcastState({ muted: !muted, cam_off: camOff, sharing }); };
  const toggleCam = () => { local.current?.getVideoTracks().forEach((t) => (t.enabled = camOff)); setCamOff(!camOff); broadcastState({ muted, cam_off: !camOff, sharing }); };
  const replaceVideo = (track) => Object.values(pcs.current).forEach((pc) => pc.getSenders().find((s) => s.track?.kind === "video")?.replaceTrack(track));
  const toggleShare = async () => {
    if (sharing) { screen.current?.getTracks().forEach((t) => t.stop()); screen.current = null; replaceVideo(local.current?.getVideoTracks()[0] || null); setSharing(false); return; }
    try { const s = await navigator.mediaDevices.getDisplayMedia({ video: true }); screen.current = s; const t = s.getVideoTracks()[0]; t.onended = () => { replaceVideo(local.current?.getVideoTracks()[0] || null); setSharing(false); screen.current = null; }; replaceVideo(t); setSharing(true); }
    catch { toast.error("Screen share cancelled"); }
  };

  return <CallCtx.Provider value={{ call, incoming, streams, localStream, muted, camOff, sharing, chat, peerState, startCall, accept, decline, hangup, joinRoom, toggleMute, toggleCam, toggleShare, sendChat }}>{children}</CallCtx.Provider>;
}
